import { useState } from 'react';
import { DayNavigator } from '../../components/mobile/DayNavigator';
import { AssignmentSheet } from '../../components/mobile/AssignmentSheet';
import { StaleScheduleNotice } from '../../components/schedule/StaleScheduleNotice';
import {
  ScheduleFiltersSheet,
  type FilterOption,
} from '../../components/mobile/ScheduleFiltersSheet';
import {
  MOBILE_EMPTY_ACTION,
  MobileEmptyState,
  MobileErrorState,
  MobileListSkeleton,
} from '../../components/mobile/MobileListStates';
import { useSwipe } from '../../components/mobile/useSwipe';
import { useProjectSchedule } from '../../hooks/useProjectSchedule';
import { useScheduleFilterState } from '../../hooks/useScheduleFilterState';
import { useOffices } from '../../hooks/useOffices';
import { useServiceLines } from '../../hooks/useServiceLines';
import { useRegions } from '../../hooks/useRegions';
import { addDays, formatShortWeekday, todayIso } from '../../lib/date';
import { EMPTY_FILTERS, filterChips, filterCount } from '../../lib/scheduleFilters';
import type { ProjectScheduleCellCrew, ProjectScheduleRow } from '../../lib/types';

/** Chips beyond this fold into a `+N more` chip that opens the sheet. */
const MAX_VISIBLE_CHIPS = 4;

/**
 * `/project-schedule` on a phone: the same day-at-a-time shell as `/schedule`, with the
 * grouping inverted. Projects are the headers and the crew assigned that day are pills
 * underneath, which answers "who's on 020417 today?" without the desktop grid's matrix.
 *
 * The control row holds Filters alone.
 */
export function ProjectScheduleMobilePage() {
  const [day, setDay] = useState(todayIso());
  const [sheetJob, setSheetJob] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Which failure the user has already waved away, by its timestamp — a later failure gets
  // to interrupt again, the same one doesn't.
  const [dismissedErrorAt, setDismissedErrorAt] = useState<number | null>(null);

  const { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds } =
    useScheduleFilterState();
  const { data: regions } = useRegions();
  const { data: offices } = useOffices();
  const { data: serviceLines } = useServiceLines();

  const query = useProjectSchedule({
    from: day,
    to: day,
    region: filters.region || undefined,
    office: filters.office || undefined,
    serviceLine: filters.serviceLine || undefined,
    personIds,
    projectIds,
  });

  const swipe = useSwipe({
    onSwipeLeft: () => setDay((d) => addDays(d, 1)),
    onSwipeRight: () => setDay((d) => addDays(d, -1)),
  });

  const rows = query.data?.rows ?? [];
  const groups = rows
    .map((row) => ({
      row,
      items: (row.cells.find((c) => c.date === day) ?? row.cells[0])?.items ?? [],
    }))
    .filter((g) => g.items.length > 0);

  // One count per assigned person, matching the pills below — not the sum of `crewCount`,
  // which counts bodies rather than rows and would disagree with what's on screen.
  const crewCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  const regionOptions: FilterOption[] = (regions ?? []).map((r) => ({
    value: r.code,
    label: r.name || r.code,
  }));
  const regionLabel = regionOptions.find((r) => r.value === filters.region)?.label;
  const chips = filterChips(filters, regionLabel);
  const activeCount = filterCount(filters);

  const dayLabel = formatShortWeekday(day);
  const hasCachedData = !!query.data;
  const showErrorBlock = !!query.error && !hasCachedData;
  // A day already on screen beats a blank screen in the field, so a failure with cached data
  // is a strip over the stale list rather than a replacement for it.
  const showErrorStrip =
    !!query.error && hasCachedData && query.errorUpdatedAt !== dismissedErrorAt;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Everything above the list stays put: the day never scrolls off, so a planner always
          knows which day they're reading and can always reach the filters. */}
      <div className="shrink-0 bg-white border-b border-slate-200">
        <DayNavigator
          date={day}
          onPrev={() => setDay((d) => addDays(d, -1))}
          onNext={() => setDay((d) => addDays(d, 1))}
          onToday={() => setDay(todayIso())}
          onDateChange={setDay}
        />

        <div className="px-3 pt-2 pb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={activeCount > 0 ? `Filters, ${activeCount} applied` : 'Filters'}
            className={`shrink-0 min-h-[44px] px-3 rounded-lg border text-sm font-medium flex items-center gap-2 ${
              activeCount > 0
                ? 'border-brand-blue text-brand-blue bg-brand-blue/5'
                : 'border-slate-300 text-brand-charcoal'
            }`}
          >
            <FilterIcon />
            <span>Filters</span>
            {activeCount > 0 && (
              <span
                aria-hidden="true"
                className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-brand-blue text-white text-xs font-semibold tabular-nums flex items-center justify-center"
              >
                {/* Capped at two digits so the button's width stays bounded. */}
                {activeCount > 99 ? '99+' : activeCount}
              </span>
            )}
          </button>
        </div>

        {chips.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x px-3 pb-2">
            {chips.slice(0, MAX_VISIBLE_CHIPS).map((chip) => (
              <span
                key={chip.key}
                className="shrink-0 inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-slate-100 text-brand-charcoal text-xs font-medium"
              >
                <span className="max-w-[140px] truncate">{chip.label}</span>
                <button
                  type="button"
                  onClick={() => changeFilters(chip.without)}
                  aria-label={`Remove filter: ${chip.label}`}
                  className="w-8 h-8 -mr-1 flex items-center justify-center text-slate-500 text-base leading-none"
                >
                  ×
                </button>
              </span>
            ))}
            {/* Never leave a filter reachable only by horizontal scroll — a hidden filter is
                how a planner concludes the day is empty. */}
            {chips.length > MAX_VISIBLE_CHIPS && (
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="shrink-0 inline-flex items-center h-8 px-3 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-medium"
              >
                +{chips.length - MAX_VISIBLE_CHIPS} more
              </button>
            )}
          </div>
        )}
      </div>

      <StaleScheduleNotice />

      <div className="flex-1 overflow-y-auto overscroll-contain" {...swipe}>
        {showErrorStrip && (
          <MobileErrorState
            title="Couldn't load the project schedule"
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
            compact
            onDismiss={() => setDismissedErrorAt(query.errorUpdatedAt)}
          />
        )}

        {showErrorBlock && (
          <MobileErrorState
            title="Couldn't load the project schedule"
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
          />
        )}

        {!showErrorBlock && query.isLoading && <MobileListSkeleton blocks={3} variant="pills" />}

        {!showErrorBlock && !query.isLoading && groups.length === 0 && (
          activeCount > 0 ? (
            <MobileEmptyState
              title="No matches"
              body={`No assignments on ${dayLabel} match your ${activeCount} ${
                activeCount === 1 ? 'filter' : 'filters'
              }.`}
            >
              <button
                type="button"
                onClick={() => changeFilters(EMPTY_FILTERS)}
                className={MOBILE_EMPTY_ACTION}
              >
                Clear filters
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className={MOBILE_EMPTY_ACTION}
              >
                Edit filters
              </button>
            </MobileEmptyState>
          ) : (
            <MobileEmptyState
              title="Nothing scheduled"
              body={`No project has crew assigned on ${dayLabel}.`}
            >
              {day !== todayIso() && (
                <button
                  type="button"
                  onClick={() => setDay(todayIso())}
                  className={MOBILE_EMPTY_ACTION}
                >
                  Back to today
                </button>
              )}
            </MobileEmptyState>
          )
        )}

        {!showErrorBlock &&
          groups.map(({ row, items }) => (
            <section key={row.projectId} aria-labelledby={`prj-${row.projectId}`}>
              <ProjectGroupHeader
                id={`prj-${row.projectId}`}
                row={row}
                crewCount={items.length}
                onClick={() => setSheetJob(row.jobNumber)}
              />
              <CrewPills jobNumber={row.jobNumber} crew={items} />
            </section>
          ))}
      </div>

      {/* aria-live node stays mounted even with nothing to say — an unmounted live region
          stops announcing, so the border/padding move inside the conditional instead. */}
      <div aria-live="polite" className="shrink-0 text-center text-xs text-slate-500">
        {groups.length > 0 && (
          <p className="px-3 py-3 border-t border-slate-200">
            {`Showing ${groups.length} ${groups.length === 1 ? 'project' : 'projects'} · ${crewCount} assigned`}
          </p>
        )}
      </div>

      <AssignmentSheet jobNumber={sheetJob} onClose={() => setSheetJob(null)} />

      <ScheduleFiltersSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={changeFilters}
        appliedSavedFilter={appliedFilter}
        onApplySavedFilter={applySavedFilter}
        regions={regionOptions}
        offices={(offices ?? []).map((o) => o.name)}
        serviceLines={(serviceLines ?? []).map((s) => s.name)}
      />
    </div>
  );
}

interface ProjectGroupHeaderProps {
  /** Ties the heading to its `<section aria-labelledby>` so the rotor can jump between projects. */
  id: string;
  row: ProjectScheduleRow;
  /** People assigned that day — the number the pills below show. */
  crewCount: number;
  /** Opens the project detail sheet. The whole header is the target. */
  onClick: () => void;
}

/**
 * The sticky caption above one project's crew for the shown day, and the tap target for its
 * detail sheet. Same three-part anatomy as an assignment card — rail, identity, context —
 * so the two views read the same way even though the grouping is inverted.
 */
function ProjectGroupHeader({ id, row, crewCount, onClick }: ProjectGroupHeaderProps) {
  const context = [row.account, row.serviceLine].filter(Boolean).join(' · ');
  const uncategorized = row.categoryName === 'Uncategorized';
  // "Assigned" rather than "crew" — the header counts people shown below it, and `crew_count`
  // (summed into the pill's `×N` suffix) is a separate, row-level, possibly-ambiguous figure.
  // Naming both "crew" reads as one number when they can legitimately differ.
  const assignedText = crewCount === 1 ? '1 assigned' : `${crewCount} assigned`;

  const ariaLabel = [
    `${row.jobNumber}, ${row.projectName}.`,
    context ? `${[row.account, row.serviceLine].filter(Boolean).join(', ')}.` : null,
    `${assignedText}.`,
    uncategorized ? 'Uncategorized.' : null,
    'Show project details',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <h2 id={id} className="sticky top-0 z-10">
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className="relative w-full text-left min-h-[64px] bg-slate-50 border-y border-slate-200 pl-3 pr-2 py-2.5 active:bg-slate-100"
      >
        {/* The rail carries an inset hairline so a near-white admin color doesn't vanish
            against the header. It's decorative — every meaning it hints at is also text. */}
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{
            backgroundColor: row.colorHex,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
          }}
        />

        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate">
            <span className="font-mono text-xs text-slate-500 tabular-nums">{row.jobNumber}</span>
            <span className="ml-2 text-[15px] font-semibold text-brand-charcoal">
              {row.projectName}
            </span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-slate-400 text-lg leading-none">
            ›
          </span>
        </span>

        <span className="mt-0.5 flex items-center gap-2">
          {context && (
            <span className="min-w-0 truncate text-xs text-brand-charcoal/70 font-normal">
              {context}
            </span>
          )}
          {uncategorized && (
            <span className="shrink-0 inline-flex items-center h-5 px-1.5 rounded text-[11px] font-medium bg-slate-100 text-slate-500 border border-dashed border-slate-300">
              Uncategorized
            </span>
          )}
          {/* Kept even when there's no account or service line to caption — the assigned count
              is the one number this header exists to state. */}
          <span className="ml-auto shrink-0 text-xs text-slate-500 tabular-nums font-normal">
            {assignedText}
          </span>
        </span>
      </button>
    </h2>
  );
}

/**
 * The crew on a project that day, as plain pills — one per named person, nothing else.
 *
 * `crewCount` is deliberately NOT shown here. It reads like a per-person multiplier, but the
 * warehouse report carries it as a *job-level* headcount copied onto every row: in the
 * reference extract it is identical across all rows of all 341 multi-crew jobs, and equals the
 * number of named people in 86% of them. Rendering it per pill turned a five-person job into
 * five pills each claiming "×5" — twenty-five bodies implied for five people. The header's
 * "{n} assigned" counts these pills and is the honest number.
 *
 * (Where `crewCount` exceeds the named people — 124 jobs in the extract — the gap is real
 * planner information: unfilled slots. Surfacing that is a feature, not this suffix.)
 *
 * Deliberately not tappable in v1: there's no per-person destination that `/schedule` doesn't
 * already reach, and a button that goes nowhere is worse than plain text.
 */
function CrewPills({ jobNumber, crew }: { jobNumber: string; crew: ProjectScheduleCellCrew[] }) {
  return (
    <ul
      aria-label={`Crew assigned to ${jobNumber}`}
      className="flex flex-wrap gap-1.5 px-3 py-2.5 bg-white border-b border-slate-200"
    >
      {crew.map((c) => (
        <li
          key={c.assignmentId}
          className="inline-flex items-center max-w-full h-8 px-3 rounded-full bg-slate-100 text-brand-charcoal text-xs font-medium"
        >
          <span className="truncate">{c.displayName}</span>
        </li>
      ))}
    </ul>
  );
}

function FilterIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden="true"
    >
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </svg>
  );
}
